import {
  CheckmarkSmIcon,
  CloseMdIcon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import cn from 'clsx';
import PrivateLockIcon from 'crono-fe-common/icons/Icon-Private-Lock';
import NotOwnedIcon from 'crono-fe-common/icons/Icon-Not-Owned';
import { Strategy } from 'crono-fe-common/types/strategy';
import { ReactNode } from 'react';

interface SequenceCardProps {
  strategy: Strategy;
  //The app resolves the owner's name through useGetUsers; the extension has no
  //such lookup, so the name is the host's job and the icon shows without a title
  currentUserId?: number;
  ownerName?: string | null;
  /**
   * What the tag count reveals on hover — each host renders its own chips, since
   * the two apps read the tag labels through different endpoints. The count
   * itself is rendered here so both surfaces show the same pill, and the whole
   * thing is left out when the sequence has no tags.
   */
  tagsTooltip?: ReactNode;
  hideOwnerSharedIcons?: boolean;
  //The extension picks a card before confirming with "Add"; the app acts on click
  selectable?: boolean;
  selected?: boolean;
  onClick?: () => void;
}

// "Roberto Carlos" → "Roberto C.", the way the picker labels an owner.
const shortOwnerName = (name: string) => {
  const [first, ...rest] = name.trim().split(/\s+/);
  const last = rest[rest.length - 1];
  return last ? `${first} ${last[0]}.` : first;
};

/**
 * One sequence in the picker, at the density the picker is designed at. Shared
 * by the web app and the extension — see SequencePickerPanel.
 */
const SequenceCard = ({
  strategy,
  currentUserId,
  ownerName,
  tagsTooltip,
  hideOwnerSharedIcons = false,
  selectable = false,
  selected = false,
  onClick,
}: SequenceCardProps) => {
  const tagCount = strategy.tags?.length ?? 0;

  return (
    <div
      className={cn(
        'group flex w-full cursor-pointer flex-col gap-0.5 rounded-lg p-2 hover:bg-neutral-100',
        selected && 'border-l-2 border-l-primary-700 bg-neutral-100',
      )}
      onClick={onClick}
    >
      <div className="flex min-w-0 items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden">
          {selectable && selected && (
            <>
              <CheckmarkSmIcon
                aria-hidden
                className="size-4 shrink-0 text-primary-700 group-hover:hidden"
              />
              <CloseMdIcon
                aria-hidden
                className="hidden size-4 shrink-0 text-gray-600 group-hover:block"
              />
            </>
          )}
          {!hideOwnerSharedIcons && !strategy.shared && (
            <span className="flex size-fit" title="Private sequence">
              <PrivateLockIcon />
            </span>
          )}
          <span
            className="truncate text-xs font-medium leading-4"
            title={strategy.name ?? undefined}
          >
            {strategy.name}
          </span>
        </div>
        {!hideOwnerSharedIcons &&
          currentUserId !== undefined &&
          strategy.userId !== currentUserId && (
            <span
              className="flex min-w-0 max-w-30 shrink-0 items-center gap-0.5 overflow-hidden rounded-lg bg-gray-200 py-0.5 pl-0.5 pr-1"
              title={
                strategy.userId
                  ? `The owner is ${ownerName ?? 'deleted user'}`
                  : 'No owner'
              }
            >
              {/* The component paints its paths with a `stroke` attribute,
                which this class overrides — a presentation attribute loses to
                a CSS rule */}
              <NotOwnedIcon
                size={12}
                className="shrink-0 [&>path]:stroke-neutral-800"
              />
              {!!strategy.userId && !!ownerName && (
                <span className="truncate text-3xs font-medium leading-3 text-neutral-800">
                  {shortOwnerName(ownerName)}
                </span>
              )}
            </span>
          )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {!!strategy.numberOfSteps && (
            <span className="h-4 w-fit rounded-xl text-xs font-normal leading-4 text-gray-700">
              {strategy.numberOfSteps} steps
            </span>
          )}
          {!!tagCount && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-pointer rounded px-1 py-0.5 text-xs leading-4 text-sky-700 bg-sky-100 hover:bg-sky-200">
                  {tagCount} {tagCount > 1 ? 'tags' : 'tag'}
                </span>
              </TooltipTrigger>
              {!!tagsTooltip && (
                <TooltipContent className="max-w-100 bg-white p-2">
                  {tagsTooltip}
                </TooltipContent>
              )}
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export default SequenceCard;
